import { mapLeaderboardApiEntry } from './worldcup.helper';
import type {
  GroupLeaderboardApiEntry,
  GroupLeaderboardPeriod,
} from '../hooks/useWorldCupGroupLeaderboardQuery';
import type { LeaderboardEntry } from '../types/worldcup.types';
import { SQUAD_TV_LEADERBOARD_PAGE_LIMIT } from '../constants/squadTvMode.constants';

interface GroupLeaderboardPagePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface GroupLeaderboardPagePayload {
  success: boolean;
  error?: string;
  data: {
    entries: GroupLeaderboardApiEntry[];
    pagination: GroupLeaderboardPagePagination;
  };
}

export type SquadTvLeaderboardApiClient = (
  endpoint: string,
) => Promise<{ data: GroupLeaderboardPagePayload }>;

export interface FetchAllGroupLeaderboardPagesParams {
  apiClient: SquadTvLeaderboardApiClient;
  competitionId: string;
  groupId: string;
  period?: GroupLeaderboardPeriod;
  pageLimit?: number;
}

export interface FetchAllGroupLeaderboardPagesResult {
  entries: LeaderboardEntry[];
  pagination: { total: number; totalPages: number };
}

/**
 * Fetches every page of a squad leaderboard (TV display needs the full member list).
 */
export async function fetchAllGroupLeaderboardPages({
  apiClient,
  competitionId,
  groupId,
  period = 'overall',
  pageLimit = SQUAD_TV_LEADERBOARD_PAGE_LIMIT,
}: FetchAllGroupLeaderboardPagesParams): Promise<FetchAllGroupLeaderboardPagesResult> {
  const allRaw: GroupLeaderboardApiEntry[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await apiClient(
      `/competitions/${competitionId}/groups/${groupId}/leaderboard?limit=${pageLimit}&page=${page}&period=${period}`,
    );
    const payload = response.data;
    if (!payload.success) {
      throw new Error(payload.error || 'Failed to fetch group leaderboard');
    }

    allRaw.push(...payload.data.entries);
    totalPages = Math.max(1, payload.data.pagination.totalPages);
    page += 1;
  }

  const entries = allRaw.map((entry, index) => ({
    ...mapLeaderboardApiEntry(entry),
    rank: entry.rank ?? index + 1,
  }));

  return {
    entries,
    pagination: {
      total: allRaw.length,
      totalPages,
    },
  };
}
